import React, { useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash, faBoxOpen, faXmark } from '@fortawesome/free-solid-svg-icons';

/**
 * One return stop. Lists the articles being returned from this location, each
 * capped at the net quantity confirmed-delivered there ("geliefert") — you can
 * only return what is actually at the location.
 *
 * `line` shape: { destination, destinationName, destinationType,
 *                 articles: [{ articleId, articleName, quantity }] }
 * `delivered` is the list of returnable rows for this destination:
 *   [{ article_id, article_name, delivered }]
 * `deliveredLookup` is keyed `${destinationId}|${articleId}`.
 */
const ReturnDestinationCard = ({
  line,
  delivered = [],
  deliveredLookup = {},
  isLoading,
  onAddArticle,
  onRemoveArticle,
  onChangeQuantity,
  onRemove,
}) => {
  // Track the most recently added article so we can focus its input.
  const [justAddedArticleId, setJustAddedArticleId] = useState(null);

  const usedArticleIds = useMemo(
    () => new Set(line.articles.map((a) => a.articleId)),
    [line.articles]
  );

  const availableArticles = useMemo(
    () => delivered.filter((row) => !usedArticleIds.has(row.article_id)),
    [delivered, usedArticleIds]
  );

  const totalUnits = line.articles.reduce(
    (sum, a) => sum + (a.quantity || 0),
    0
  );

  const handleAddArticle = (e) => {
    const articleId = e.target.value;
    if (!articleId) return;
    const row = delivered.find((r) => r.article_id === articleId);
    if (!row) return;
    setJustAddedArticleId(articleId);
    onAddArticle(line.destination, row);
    e.target.value = '';
  };

  return (
    <div className='card mb-3'>
      <div className='card-header d-flex justify-content-between align-items-center'>
        <div>
          <span className='fw-bold'>{line.destinationName}</span>
          <span
            className={`badge ms-2 ${
              line.destinationType === 'backstage' ? 'bg-info' : 'bg-secondary'
            }`}
          >
            {line.destinationType === 'backstage' ? 'Backstage' : 'Crew'}
          </span>
        </div>
        <button
          type='button'
          className='btn btn-sm btn-outline-danger'
          title='Lieferstelle entfernen'
          onClick={() => onRemove(line.destination)}
        >
          <FontAwesomeIcon icon={faTrash} />
        </button>
      </div>

      <div className='card-body'>
        {line.articles.length === 0 ? (
          <p className='text-muted mb-3'>
            {isLoading
              ? 'Gelieferte Mengen werden geladen…'
              : delivered.length === 0
                ? 'Keine gelieferten Artikel an dieser Lieferstelle – nichts zurückzugeben.'
                : 'Noch keine Artikel. Unten Artikel zur Retoure hinzufügen.'}
          </p>
        ) : (
          <table className='table table-sm align-middle mb-3'>
            <tbody>
              {line.articles.map((article) => {
                const row =
                  deliveredLookup[`${line.destination}|${article.articleId}`];
                const cap = row ? row.delivered : 0;
                const overCap = article.quantity > cap;
                return (
                  <tr key={article.articleId}>
                    <td>{article.articleName}</td>
                    <td
                      className={`text-nowrap small ${
                        overCap ? 'text-danger fw-bold' : 'text-muted'
                      }`}
                    >
                      {cap}
                      <FontAwesomeIcon
                        icon={faBoxOpen}
                        className='ms-1'
                        title='geliefert (max. rücknehmbar)'
                      />
                    </td>
                    <td style={{ width: '7rem' }}>
                      <input
                        className={`form-control form-control-sm ${
                          overCap ? 'border-danger' : ''
                        }`}
                        type='number'
                        min='0'
                        max={cap}
                        autoFocus={article.articleId === justAddedArticleId}
                        value={article.quantity}
                        onChange={(e) =>
                          onChangeQuantity(
                            line.destination,
                            article.articleId,
                            parseInt(e.target.value, 10) || 0
                          )
                        }
                      />
                    </td>
                    <td className='small text-nowrap'>
                      {overCap && (
                        <span className='text-danger fw-bold'>
                          mehr als geliefert
                        </span>
                      )}
                    </td>
                    <td className='text-end' style={{ width: '2.5rem' }}>
                      <button
                        type='button'
                        className='btn btn-sm btn-link text-secondary p-0'
                        title='Artikel entfernen'
                        onClick={() =>
                          onRemoveArticle(line.destination, article.articleId)
                        }
                      >
                        <FontAwesomeIcon icon={faXmark} />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}

        <select
          className='form-select form-select-sm'
          value=''
          disabled={availableArticles.length === 0}
          onChange={handleAddArticle}
        >
          <option value='' disabled>
            {delivered.length === 0
              ? 'Keine rücknehmbaren Artikel'
              : availableArticles.length === 0
                ? 'Alle Artikel hinzugefügt'
                : '+ Artikel hinzufügen…'}
          </option>
          {availableArticles.map((row) => (
            <option key={row.article_id} value={row.article_id}>
              {row.article_name} (geliefert: {row.delivered})
            </option>
          ))}
        </select>
      </div>

      <div className='card-footer text-muted small'>
        {line.articles.length} Positionen · {totalUnits} Einheiten
      </div>
    </div>
  );
};

export default ReturnDestinationCard;
